import { useState, useCallback, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

import { sanitizedDescription } from '../utils/helpers';
import { DESCRIPTION_LIMIT } from '../utils/constants';
import Text from '../../../shared/Text';
import TinyMceEditor from '../../TinyMceEditor/TinyMceEditor';
import { createPasteProcessor } from '../../TinyMceEditor/pasteProcessors';

const Description = ({ value, onChange, onInitialValue, error }) => {
  const { t } = useTranslation('tileForm');
  const theme = useTheme();
  const [length, setLength] = useState(0);
  const [editorKey, setEditorKey] = useState(0);
  const [description, setDescription] = useState(value);
  const getDescriptionLength = desc => sanitizedDescription(desc).length;

  const debouncedOnChange = useCallback(
    debounce(newValue => {
      onChange({ target: { name: 'supportingContent', value: newValue } });
    }, 500),
    [],
  );

  const handleUpdate = (newValue, editor) => {
    const editorLength = getDescriptionLength(editor.getContent());

    setDescription(newValue);
    debouncedOnChange(newValue);
    setLength(editorLength);
  };

  // Handles the resize event manually here and forces re-render
  // by triggering a state update
  useEffect(() => {
    const debouncedResize = debounce(() => {
      setEditorKey(prev => prev + 1);
    }, 300);

    window.addEventListener('resize', debouncedResize);
    return () => window.removeEventListener('resize', debouncedResize);
  }, []);

  return (
    <Wrapper error={!!error}>
      <TinyMceEditor
        editorKey={editorKey}
        onInit={(evt, editor) => {
          setLength(getDescriptionLength(editor.getContent()));
          onInitialValue(editor.getContent());
        }}
        value={description}
        onChange={handleUpdate}
        config={{
          placeholder: 'Topic Description',
          plugins: ['link', 'lists', 'autolink', 'autoresize', 'paste'],
          toolbar: 'bold italic underline | bullist numlist | link',
          paste_postprocess: createPasteProcessor(),
          content_style: `
            body,div,span,p,ul,ol,li,code {
              color: ${theme.colors.black['100']};
              font-family:Montserrat,Arial,sans-serif;
              font-weight: 400;
              font-size:16px;
              margin: 0;
              -webkit-font-smoothing: antialiased;
            }
            body { margin-top: 8px; }
            strong { font-weight: 600; }
            a {
              color: ${theme.colors.primary[100]};
              outline: none;
              text-decoration: none;
            }
            p {
              line-height: 24px;
              margin-bottom: 16px;
              margin-top: 0px;
              letter-spacing: 0.1px;
            }
            ul {
              line-height: 24px;
              list-style-type: disc;
              padding-left: 24px;
            }
            ol {
              line-height: 24px;
              list-style-type: decimal;
              padding-left: 24px;
            }
            li {
              margin-bottom: 0.7em;
              line-height: 1.4;
            }`,
        }}
      />
      <InputFooter error={!!error}>
        {error && (
          <Text variant="button" color="error.default">
            {error}
          </Text>
        )}
        <Text variant="small4" color={error ? 'error.default' : 'black.60'}>
          {DESCRIPTION_LIMIT - length} {t('charactersLeft')}
        </Text>
      </InputFooter>
    </Wrapper>
  );
};

export default Description;

const Wrapper = styled.div`
  margin: 0 0 16px 0;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: ${props => props.error && '2px solid red'};

  .tox-tinymce {
    border: none;

    .tox-edit-area::before {
      border: none;
    }

    .tox-toolbar__primary {
      background: none;
      border-bottom: solid 1px;
      border-color: ${themeGet('colors.black.10')};

      .tox-toolbar__group {
        border-color: ${themeGet('colors.black.10')} !important;
      }
    }

    .tox-statusbar {
      display: none;
    }
  }
`;

const InputFooter = styled.div`
  display: flex;
  justify-content: ${props => (props.error ? 'space-between' : 'flex-end')};
  margin: 0 0 8px;
`;
